/**
 * Tách luồng `text/event-stream` thành khung và đọc khung thành sự kiện có kiểu.
 *
 * Thuần, không đụng DOM hay mạng: lớp vận chuyển (`su-kien.ts`) giải mã byte
 * bằng `TextDecoder` rồi nạp chuỗi vào đây. Tách riêng để test được bằng
 * `node:test` (`khung-sse.test.ts`).
 *
 * Theo bộ tách của chuẩn HTML (mục "Parsing an event stream"):
 * - dòng kết thúc bằng `\r\n`, `\n` hoặc `\r`; khung kết thúc bằng dòng trống;
 * - dòng bắt đầu bằng `:` là chú thích (máy chủ gửi `: ping` mỗi 15 giây);
 * - `data:` nhiều dòng nối lại bằng `\n`; bỏ đúng một dấu cách sau dấu `:`;
 * - khung không có `data` thì không phát.
 */

export interface KhungSse {
  /** Tên sự kiện; không có dòng `event:` thì là `message`. */
  event: string;
  data: string;
  /** `id:` gần nhất của luồng — gửi lại qua `Last-Event-ID` khi nối lại. */
  id: string | undefined;
}

export class BoTachKhungSse {
  private dem = '';
  private dauLuong = true;
  private suKien = '';
  private duLieu: string[] = [];
  private coDuLieu = false;
  /** `id` mang qua các khung, đúng như chuẩn: khung sau không có `id:` vẫn giữ id trước. */
  idGanNhat: string | undefined;

  /** Nạp thêm một đoạn chữ, trả các khung đã trọn. Phần dở dang giữ lại cho lần nạp sau. */
  nap(doan: string): KhungSse[] {
    let s = this.dem + doan;
    if (this.dauLuong && s.length > 0) {
      if (s.charCodeAt(0) === 0xfeff) s = s.slice(1);
      this.dauLuong = false;
    }

    const ra: KhungSse[] = [];
    let batDau = 0;
    for (let i = 0; i < s.length; i++) {
      const c = s.charCodeAt(i);
      if (c !== 0x0a && c !== 0x0d) continue;
      // `\r` ở cuối đoạn có thể là nửa đầu của `\r\n` bị cắt giữa hai đoạn: chờ đoạn sau.
      if (c === 0x0d && i === s.length - 1) break;
      this.xuLyDong(s.slice(batDau, i), ra);
      if (c === 0x0d && s.charCodeAt(i + 1) === 0x0a) i++;
      batDau = i + 1;
    }
    this.dem = s.slice(batDau);
    return ra;
  }

  private xuLyDong(dong: string, ra: KhungSse[]): void {
    if (dong === '') {
      if (this.coDuLieu) {
        ra.push({ event: this.suKien || 'message', data: this.duLieu.join('\n'), id: this.idGanNhat });
      }
      this.suKien = '';
      this.duLieu = [];
      this.coDuLieu = false;
      return;
    }
    if (dong.startsWith(':')) return;

    const haiCham = dong.indexOf(':');
    const truong = haiCham === -1 ? dong : dong.slice(0, haiCham);
    let giaTri = haiCham === -1 ? '' : dong.slice(haiCham + 1);
    if (giaTri.startsWith(' ')) giaTri = giaTri.slice(1);

    switch (truong) {
      case 'event':
        this.suKien = giaTri;
        break;
      case 'data':
        this.duLieu.push(giaTri);
        this.coDuLieu = true;
        break;
      case 'id':
        if (!giaTri.includes('\u0000')) this.idGanNhat = giaTri;
        break;
      default:
        // `retry:` và trường lạ: bỏ qua. Nhịp nối lại do lớp vận chuyển tự quyết.
        break;
    }
  }
}

/* ------------------------------------------------------------------ *
 * Sự kiện của `GET /api/v1/users/me/events`.
 * ------------------------------------------------------------------ */

export interface SuKienSanSang {
  type: 'ready';
  data: { user_id: string; tenant_id?: string };
}
export interface SuKienPhienDoi {
  type: 'session.changed';
  data: { reason: 'login' | 'logout' | 'revoked'; session_id?: string; occurred_at?: string };
}
export interface SuKienPhienKhoa {
  type: 'session.locked';
  data: { occurred_at?: string };
}
export interface SuKienTenantDoi {
  type: 'tenant.changed';
  data: { reason: 'status' | 'security'; occurred_at?: string };
}
export interface SuKienThongBao {
  type: 'notification';
  data: { id: string; title: string; body?: string; level?: string; occurred_at?: string };
}

export type SuKienNguoiDung =
  | SuKienSanSang
  | SuKienPhienDoi
  | SuKienPhienKhoa
  | SuKienTenantDoi
  | SuKienThongBao;

type LaDoiTuong = Record<string, unknown>;

function laDoiTuong(v: unknown): v is LaDoiTuong {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Đọc một khung thành sự kiện có kiểu. Tên sự kiện lạ, `data` không phải JSON
 * hay thiếu trường bắt buộc thì trả `null` — máy chủ thêm loại sự kiện mới
 * không làm vỡ bản giao diện cũ.
 */
export function docSuKien(khung: KhungSse): SuKienNguoiDung | null {
  let data: unknown;
  try {
    data = JSON.parse(khung.data);
  } catch {
    return null;
  }
  if (!laDoiTuong(data)) return null;

  switch (khung.event) {
    case 'ready':
      return typeof data.user_id === 'string' ? { type: 'ready', data: data as SuKienSanSang['data'] } : null;
    case 'session.changed':
      return data.reason === 'login' || data.reason === 'logout' || data.reason === 'revoked'
        ? { type: 'session.changed', data: data as SuKienPhienDoi['data'] }
        : null;
    case 'session.locked':
      return { type: 'session.locked', data: data as SuKienPhienKhoa['data'] };
    case 'tenant.changed':
      return data.reason === 'status' || data.reason === 'security'
        ? { type: 'tenant.changed', data: data as SuKienTenantDoi['data'] }
        : null;
    case 'notification':
      return typeof data.id === 'string' && typeof data.title === 'string'
        ? { type: 'notification', data: data as SuKienThongBao['data'] }
        : null;
    default:
      return null;
  }
}
